/**
 * The Dispatch pages' pure half: a call in words, a load's lane, and the check-call schedule
 * matched against the calls that actually happened. Pure so it can be tested without a browser.
 *
 * ⛔ A CALL IS DESCRIBED BY WHAT HAPPENED, never by what was hoped for. A failed dial reads "did not
 * go out", a voicemail reads "voicemail", and only a person answering reads "talked". What the driver
 * SAID is in the transcript ("Read what was said"): nothing summarises it yet.
 */

export const REASON_LABEL = {
  LOAD_OFFER: "Load offer", CHECK_IN: "Check call", ETA_STATUS: "ETA call", PICKUP_CONFIRM: "Pickup call",
  DELIVERY_CONFIRM: "Delivery call", ISSUE: "Call about an issue",
};

/** The Call form's reasons. "A change" is a check call that carries what Alfred tells them. */
export const CALL_REASONS = [
  { v: "CHECK_IN", label: "Check in" },
  { v: "ETA_STATUS", label: "Get an updated ETA" },
  { v: "PICKUP_CONFIRM", label: "Confirm pickup" },
  { v: "DELIVERY_CONFIRM", label: "Confirm delivery" },
  { v: "CHANGE", label: "Tell them about a change" },
];
/** The platform's reason for a form choice (backend calls.mjs REASONS). */
export const platformReason = (v) => (v === "CHANGE" ? "CHECK_IN" : v);
export const MESSAGE_MAX = 300;

/** What came of one call, in a few words. */
export function outcomeWords(c) {
  if (!c) return "";
  // Queued with no id from the platform: asked, and not yet answered (backend calls.mjs platform_timeout).
  if (c.status === "queued" && !c.coreCallId) return "no word from the platform yet (it may have gone through)";
  if (c.status === "queued" || c.status === "placed") return "on the call now";
  // ⛔ Closed with no word from the platform (backend scheduled.mjs): it may have gone, so never "did not go out".
  if (c.status === "failed" && c.error === "no_result_from_platform") return "no result from the platform (it may have gone through)";
  if (c.status === "failed") return `did not go out${c.error ? ` (${String(c.error).replace(/_/g, " ")})` : ""}`;
  if (c.status === "no_answer") return "no answer";
  if (c.answeredSubtype === "voicemail" || c.outcome === "voicemail") return "voicemail";
  if (c.answeredSubtype === "ivr" || c.outcome === "ivr") return "a phone menu, no person";
  if (c.status === "completed") return "talked";
  return String(c.status || "");
}

/** "Check call at 12:04 PM: talked". `when` formats a time (ui.jsx when). */
export function callLine(c, when) {
  if (!c) return "No calls yet";
  const at = when(c.startedAt || c.createdAt);
  return `${REASON_LABEL[c.reason] || "Call"}${at ? ` at ${at}` : ""}: ${outcomeWords(c)}`;
}

const cityOf = (s) => String(s?.city || s?.name || "").trim();
const windowOf = (s) => { const w = String(s?.window ?? "").trim(); return w && w !== "—" ? w : ""; };

/** "[Pickup city] → [Delivery city]. Pickup 9:00 AM, deliver by 5:00 PM." Only what is on the load. */
export function laneLine(load) {
  const stops = Array.isArray(load?.stops) ? load.stops : [];
  const first = stops[0], last = stops.length > 1 ? stops[stops.length - 1] : null;
  const lane = [cityOf(first), cityOf(last)].filter(Boolean).join(" → ");
  const pick = load?.detail?.pickupAt || windowOf(stops.find((s) => s?.type === "pickup") || first);
  const drop = load?.detail?.deliverBy || windowOf([...stops].reverse().find((s) => s?.type === "drop") || last);
  const times = [pick ? `Pickup ${pick}` : null, drop ? `deliver by ${drop}` : null].filter(Boolean).join(", ");
  return [lane ? `${lane}.` : null, times ? `${times}.` : null].filter(Boolean).join(" ") || "No stops on file.";
}

/**
 * The check calls a load gets, from the carrier's rule (Rules → Check calls; the default is the
 * schematic's "at pickup, halfway, 1 hour before delivery and after delivery"), each matched to a
 * call that happened: done when a person answered a call of that kind on this load.
 */
export const DEFAULT_CHECK_CALLS = ["pickup", "halfway", "before_delivery", "after_delivery"];
export const CHECK_CALL = {
  pickup: { label: "At pickup", reason: "PICKUP_CONFIRM" },
  halfway: { label: "Halfway", reason: "CHECK_IN" },
  before_delivery: { label: "Before delivery", reason: "ETA_STATUS" },
  after_delivery: { label: "Delivered and empty", reason: "DELIVERY_CONFIRM" },
};
const talked = (c) => c.status === "completed" && !["voicemail", "ivr"].includes(c.answeredSubtype || c.outcome);

/**
 * ⛔ ONLY CALLS THAT HAPPENED COUNT (review): a seeded call was never placed, so it never ticks a
 * step; and a call that carried words for the driver ("Tell them about a change" goes as a check-in)
 * is not the halfway check call.
 */
export function scheduleFor(load, calls = [], steps = DEFAULT_CHECK_CALLS) {
  const onLoad = (calls || []).filter((c) => String(c.loadId) === String(load?.id) && !c.seeded);
  let nextGiven = false;
  return steps.filter((k) => CHECK_CALL[k]).map((k) => {
    const def = CHECK_CALL[k];
    const done = onLoad.filter((c) => c.reason === def.reason && talked(c) && !(k === "halfway" && c.note))
      .sort((a, b) => new Date(b.startedAt || b.createdAt) - new Date(a.startedAt || a.createdAt))[0] || null;
    let state = done ? "done" : "later";
    if (!done && !nextGiven) { state = "next"; nextGiven = true; }
    return { key: k, label: def.label, reason: def.reason, state, call: done };
  });
}

/**
 * "Later, at 3:45 PM": the next time the clock shows it, today or tomorrow, as an instant. The page
 * says which ("today" or "tomorrow") before the call is set.
 */
export function nextAt(hhmm, now = new Date()) {
  const m = /^(\d{2}):(\d{2})$/.exec(String(hhmm || ""));
  if (!m) return null;
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate(), Number(m[1]), Number(m[2]));
  const tomorrow = d.getTime() <= now.getTime() + 60000;
  if (tomorrow) d.setDate(d.getDate() + 1);
  return { at: d, day: tomorrow ? "tomorrow" : "today" };
}

/**
 * Trucks or trailers from the roster, ONE ROW PER NUMBER (review): two drivers on truck 214 are one
 * truck with two drivers, not two trucks. A trailer is counted only by its number; drivers with only a
 * trailer type on file are counted apart (`typeOnly`), since a type is not a trailer.
 */
export function equipmentRows(drivers = [], kind = "trucks") {
  const isTruck = kind === "trucks";
  const numOf = (d) => String((isTruck ? d.truck : d.trailerNo) ?? "").trim();
  const groups = new Map();
  for (const d of drivers || []) {
    const num = numOf(d);
    if (!num) continue;
    const key = num.toUpperCase();
    if (!groups.has(key)) groups.set(key, { key, name: `${isTruck ? "Truck" : "Trailer"} ${num}`, types: new Set(), drivers: [] });
    const g = groups.get(key);
    g.drivers.push(d);
    if (!isTruck && d.trailerType) g.types.add(String(d.trailerType).trim());
  }
  const rows = [...groups.values()].map((g) => ({ key: g.key, name: g.name, type: [...g.types].join(", ") || null, drivers: g.drivers }))
    .sort((a, b) => a.name.localeCompare(b.name, "en", { numeric: true }));
  const typeOnly = isTruck ? 0 : (drivers || []).filter((d) => !numOf(d) && String(d.trailerType || "").trim()).length;
  return { rows, typeOnly };
}

/** Newest first, the calls about one load. */
export const callsOn = (load, calls = []) => (calls || []).filter((c) => String(c.loadId) === String(load?.id))
  .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
